/**
 * Character annotation, ARCHITECTURE.md §4.3.2.
 *
 * Body points are percent of ONE sprite frame (512×512 source; the same
 * numbers apply to the 256 px re-pack). `affordances` and `lints` are
 * DERIVED on save.
 */
import { z } from 'zod';
import { envelope, Facing, Id, Lint, Pct, Provenance, Pt } from './common';

/** A real-world measurement with provenance (height resolution chain in §4.3.2). */
export const Measured = z
  .strictObject({
    value: z.number().positive(),
    unit: z.literal('cm'),
    provenance: Provenance,
  })
  .meta({ id: 'Measured' });
export type Measured = z.infer<typeof Measured>;

/** Opaque bounds inside a frame or raster box, percent. */
export const PctBox = z
  .strictObject({ x: Pct, y: Pct, w: Pct, h: Pct })
  .meta({ id: 'PctBox' });
export type PctBox = z.infer<typeof PctBox>;

export const BodyPoints = z
  .strictObject({
    /** Ground contact pivot, default [50, 94]. */
    feet: Pt,
    /** Bubble anchor, default [50, 8]. */
    head_top: Pt,
    hand: Pt.optional(),
    hand2: Pt.optional(),
    mouth: Pt.optional(),
    /** Opaque bounds of idle frame 0. */
    bbox: PctBox.optional(),
  })
  .meta({ id: 'BodyPoints' });
export type BodyPoints = z.infer<typeof BodyPoints>;

/** Named body-part anchors, in percent of the original full frame. */
export const Landmarks = z.record(z.string().min(1).max(64), Pt);
export type Landmarks = z.infer<typeof Landmarks>;

/** Default body points when a character has none (§4.3.2). */
export const BODY_POINTS_DEFAULT: BodyPoints = { feet: [50, 94], head_top: [50, 8] };

export const VoiceRole = z.enum(['narrator', 'child', 'small_animal', 'big_animal', 'adult', 'creature']);
export type VoiceRole = z.infer<typeof VoiceRole>;

export const SemanticMarker = z.strictObject({
  id: z.string().min(1).max(80),
  kind: z.enum(['contact', 'release', 'cycle_complete']),
  action: z.string().min(1).max(80),
  frame: z.int().min(0),
  socket: z.string().min(1).max(64).optional(),
  source_id: z.string().min(1).max(256),
  provenance: Provenance,
  review_state: z.enum(['pending', 'reviewed']),
  reviewed_by: z.string().min(1).max(256).optional(),
}).refine((m) => m.review_state !== 'reviewed' || Boolean(m.reviewed_by), {
  message: 'Reviewed markers require reviewer attribution', path: ['reviewed_by'],
}).meta({ id: 'SemanticMarker' });
export type SemanticMarker = z.infer<typeof SemanticMarker>;

/** Pinned, clip-local timing shared by bone and spritesheet actions. */
export const NormalizedMarker = SemanticMarker.extend({
  offset_s: z.number().nonnegative(),
  clip_duration_s: z.number().positive(),
  render: z.enum(['bones', 'spritesheet']),
}).refine((m) => m.offset_s < m.clip_duration_s, { message: 'Marker must be within its clip' });
export type NormalizedMarker = z.infer<typeof NormalizedMarker>;

export const Clip = z.strictObject({
  /** Directory name: 'walk_3q_left'. */
  id: Id,
  /** vocab(action).key ('walk'); unknown -> 'x:<name>'. */
  action: Id,
  facing: Facing,
  mirrored: z.boolean(),
  deprecated: z.boolean(),
  /** False for config-only dirs / enabled:false / broken atlas. */
  usable: z.boolean(),
  frames: z.int().min(0),
  fps: z.number().positive(),
  loop: z.boolean(),
  /** Per-clip override (sit/sleep lower the feet). */
  points: BodyPoints.partial().optional(),
  landmarks: Landmarks.optional(),
  frame_landmarks: z.record(z.string().regex(/^(0|[1-9][0-9]*)$/), Landmarks).optional(),
  semantic_markers: z.array(SemanticMarker).optional(),
  provenance: Provenance,
}).refine((c) => (c.semantic_markers ?? []).every((m) => m.frame < c.frames), { message: 'Semantic marker is outside this clip', path: ['semantic_markers'] })
  .refine((c) => new Set(c.semantic_markers?.map((m) => m.id)).size === (c.semantic_markers?.length ?? 0), { message: 'Duplicate semantic marker IDs', path: ['semantic_markers'] });
export type Clip = z.infer<typeof Clip>;

export const CharacterBody = z.strictObject({
  slug: Id,
  /** 'king_asian' -> family 'king'. */
  family: Id,
  /** Canonical asset uid when this is a skin: 'char:people/royalty/king'. */
  skin_of: z.string().optional(),
  category: z.string(),
  display_name: z.string().min(1),
  description: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  height_cm: Measured,
  /** Humans 'camera'; animals a side. */
  facing_default: Facing,
  handedness: z.enum(['left', 'right']).optional(),
  points: BodyPoints,
  landmarks: Landmarks.optional(),
  clips: z.array(Clip),
  /** Deletion markers retained by the shared annotation merge service. */
  tombstones: z.array(z.string()).optional(),
  /** action -> clip id, resolved with fallback chains (§4.4.4). */
  defaults: z.record(z.string(), z.string()),
  voice: z
    .strictObject({
      role: VoiceRole,
      kokoro: z.string().optional(),
      speed: z.number().positive().optional(),
    })
    .optional(),
  tags: z.array(z.string()).optional(),
  /** DERIVED: can_walk, can_run, can_fly, can_swim, can_talk, can_sleep, can_emote, can_hold. */
  affordances: z.array(z.string()),
  lints: z.array(Lint),
});
export type CharacterBody = z.infer<typeof CharacterBody>;

export const CharacterAnnotation = envelope('character', CharacterBody).meta({
  id: 'CharacterAnnotation',
  title: 'Character annotation',
  description: 'Annotation document for a character or skin (story-studio.character/1).',
});
export type CharacterAnnotation = z.infer<typeof CharacterAnnotation>;
